import { Suspense } from "react";
import { Outlet } from "react-router";
import { SignedIn, SignedOut } from "@clerk/clerk-react";
import { AccountMenu } from "./account-menu";
import Link from "./shims/link";
import { OfflineBanner } from "@/components/pwa/offline-banner";
import { InstallPrompt } from "./install-prompt";
import { ThemeToggle } from "@/components/theme/theme-toggle";
import { useMe } from "./session-gate";

/**
 * Next `(app)/layout.tsx`에 대응하는 껍데기.
 *
 * 바뀐 것은 계정 메뉴뿐이다. Next 쪽은 서버 액션으로 로그아웃하고 이름 변경
 * 다이얼로그를 직접 띄우는데, 여기서는 `AccountMenu`(account-menu.tsx)가 로그아웃·프로필을
 * 맡는다 — 인증을 Clerk에 넘긴 이상 그쪽 UI를 쓰는 게 맞다.
 *
 * 다만 Clerk 프로필은 **Clerk의 계정 정보**지 우리 `users.name`이 아니다. 그래서
 * 닉네임·정산 계좌는 `/profile`로 따로 뺐다 — 안 그러면 여행이 없는 사람은
 * 닉네임을 바꿀 길이 없다(그 화면 주석 참고).
 *
 * 관리자 링크는 role로 보인다. **이건 장식이다** — 숨긴다고 막히는 게 아니라
 * `/api/admin/*`가 관리자가 아니면 404를 준다.
 */
export function AppLayout() {
  const me = useMe();

  return (
    <div className="flex min-h-dvh flex-1 flex-col">
      <OfflineBanner />
      {/* 모바일에서만, 아직 설치 안 했고 닫은 적 없을 때만 뜬다. */}
      <InstallPrompt />
      <header className="sticky top-0 z-30 border-b border-border/70 bg-background/80 backdrop-blur-md print:hidden transition-colors">
        {/*
          좁은 화면에서 **글자 단위로 줄바꿈되던 것**을 막는다(390px에서 "여행가자"가
          두 줄, "내 여 행"처럼 쪼개졌다 — 링크를 하나 더 늘리면서 폭이 모자랐다).
          간격을 좁히고 줄바꿈을 금지한 뒤, 그래도 넘치면 가로로 스크롤시킨다.
        */}
        <div className="mx-auto flex h-14 w-full max-w-5xl items-center gap-3 px-4 sm:gap-6 sm:px-6">
          <Link
            href="/trips"
            className="shrink-0 whitespace-nowrap text-lg font-bold tracking-tight bg-gradient-to-r from-primary to-indigo-500 bg-clip-text text-transparent hover:opacity-90 transition-opacity"
          >
            여행가자
          </Link>
          {/* 왼쪽은 **여행 관련** 이동. 계정 관련은 오른쪽 묶음으로 뺐다. */}
          <nav className="flex min-w-0 gap-3 overflow-x-auto no-scrollbar text-sm font-medium text-muted-foreground sm:gap-4">
            <SignedIn>
              <Link href="/trips" className="whitespace-nowrap hover:text-foreground transition-colors">
                내 여행
              </Link>
            </SignedIn>
            <Link href="/help" className="whitespace-nowrap hover:text-foreground transition-colors">
              도움말
            </Link>
            {me?.role === "admin" && (
              <Link href="/admin" className="whitespace-nowrap hover:text-foreground transition-colors">
                관리자
              </Link>
            )}
          </nav>
          {/*
            내 정보(닉네임·정산 계좌)는 계정 항목이라 테마·계정 버튼과 같은 묶음에
            둔다. 계정 메뉴의 "계정 관리"는 **Clerk 계정**이고 이쪽은 우리
            `users` 행이라, 둘이 나란히 있어야 어디로 가야 할지가 덜 헷갈린다.
          */}
          <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
            <SignedIn>
              <Link
                href="/profile"
                className="whitespace-nowrap text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                내 정보
              </Link>
            </SignedIn>
            <ThemeToggle />
            {/*
              공개 도움말에서도 이 껍데기를 쓴다. 로그인 전이면 계정 버튼 대신
              들어갈 길을 준다 — 빈 자리만 남으면 어디로 가야 할지 알 수 없다.
            */}
            <SignedIn>
              <AccountMenu />
            </SignedIn>
            <SignedOut>
              <Link
                href="/sign-in"
                className="whitespace-nowrap rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity"
              >
                앱 사용하기
              </Link>
            </SignedOut>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-5xl flex-1 px-6 py-6">
        {/* 화면 모듈을 나눠 실었다. **껍데기는 그대로 두고** 본문만 바뀐다 —
            Suspense를 라우터 바깥에 두면 머리글까지 사라져 깜빡인다. */}
        <Suspense fallback={<p className="p-6 text-sm text-muted-foreground">불러오는 중…</p>}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
